// src/screens/admin/CotizadorOrcamento.jsx
// "Crear orçamento": la tarjeta (imagen) para el cliente, desde el Cotizador.
// Sirve para clientes directos y revendedores. Dos modos: Básico (para mandar
// antes de que pague: solo lo justo para decidir) y Detallado (para entregar
// cuando ya pagó). Nunca sale ganancia, costo, comisión, hostal ni fuente.
import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { toPng } from 'html-to-image';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import { toast } from '../../components/UI';
import { actualizarOpcion, actualizarCotizacion } from '../../services/cotizadorService';
import { mejorOpcion, fmtFecha, usaMargen } from '../../services/cotizadorCalc';
import {
  construirTarjeta, tarjetaHtml, alternarOpcion, nombreArchivo, TARJETA_CSS, MAX_OPCIONES,
} from '../../services/tarjetaOrcamento';
import { entregarArchivo, Interruptor, CSS_ARTE } from './ExportarArte';
import { Campo, inputStyle, cardStyle, btnChico, labelStyle, useAutoGuardado, IndicadorGuardado, diaSemana, resumenFila } from './CotizadorUI';

const NOTA_DEFECTO = 'Importante: el precio está en reales. Puede variar según la disponibilidad.';
const LOGO = `${process.env.PUBLIC_URL || ''}/logo-tarjeta.png`;
const esperarFuentes = () => Promise.all(
  [400, 500, 600, 700].map((w) => document.fonts?.load(`${w} 24px Poppins`)),
).catch(() => null);

export default function CotizadorOrcamento({ cot, horarios, resultados, onCambiarOpcion, onCambiarCot, onBack }) {
  const esRev = usaMargen(cot.tipo);
  const { estado, programar, flush } = useAutoGuardado();

  // Solo se pueden usar opciones con ómnibus que llega a tiempo.
  const validas = useMemo(() => cot.opciones.map((o, i) => (!!resultados[i]?.bus || !!resultados[i]?.saleBVB) && resultados[i]?.estado !== 'dia_invalido' && !!o.dia_vuelo), [cot.opciones, resultados]);
  const [ids, setIds] = useState(() => {
    const m = mejorOpcion(resultados);
    const i = m >= 0 && validas[m] ? m : validas.findIndex(Boolean);
    return i >= 0 ? [cot.opciones[i].id] : [];
  });
  const [modo, setModo] = useState(cot.pagado ? 'detallado' : 'basico');
  const [mostrarPrecio, setMostrarPrecio] = useState(!cot.pagado);
  const [mostrarDolares, setMostrarDolares] = useState(false);
  const [nota, setNota] = useState(NOTA_DEFECTO);
  const [pasajerosTxt, setPasajerosTxt] = useState((cot.pasajeros || []).join('\n'));
  const [editar, setEditar] = useState(false);
  const [exportando, setExportando] = useState('');
  const [escala, setEscala] = useState(0.3);
  const [alto, setAlto] = useState(0);
  const contenedor = useRef(null);
  const tarjetaRef = useRef(null);

  const elegirModo = (m) => { setModo(m); setMostrarPrecio(m === 'basico'); };
  const marcarPagado = (v) => {
    onCambiarCot({ pagado: v });
    programar('cot', { pagado: v }, (p) => actualizarCotizacion(cot.id, p));
    elegirModo(v ? 'detallado' : 'basico');
  };
  const editarPasajeros = (t) => {
    setPasajerosTxt(t);
    const lista = t.split('\n').map((x) => x.trim()).filter(Boolean);
    onCambiarCot({ pasajeros: lista });
    programar('cot', { pasajeros: lista }, (p) => actualizarCotizacion(cot.id, p));
  };

  const elegidas = ids.map((id) => cot.opciones.findIndex((o) => o.id === id)).filter((i) => i >= 0);
  const modelo = useMemo(() => construirTarjeta({
    cliente: { tipo: cot.tipo, usd: cot.usd, tasa: cot.tasa, pasajeros: cot.pasajeros, llegada: cot.llegada },
    opciones: elegidas.map((i) => ({ opcion: cot.opciones[i], resultado: resultados[i] })),
    modo, horarios, mostrarPrecio, mostrarDolares, nota,
  }), [cot, ids, resultados, modo, horarios, mostrarPrecio, mostrarDolares, nota]);
  const html = useMemo(() => tarjetaHtml(modelo, { logoUrl: LOGO }), [modelo]);

  // La vista previa se ve a ancho de teléfono; la imagen final es de 1080 px.
  useLayoutEffect(() => {
    const ajustar = () => {
      if (contenedor.current) setEscala(contenedor.current.clientWidth / 1080);
      if (tarjetaRef.current) setAlto(tarjetaRef.current.offsetHeight);
    };
    ajustar();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(ajustar) : null;
    if (ro) { ro.observe(contenedor.current); ro.observe(tarjetaRef.current); }
    esperarFuentes().then(ajustar);
    return () => ro?.disconnect();
  }, [html]);

  // Textos corregidos a mano: se guardan en la opción, por modo y por tramo.
  const ajustesDe = (op) => op.tarjeta_ajustes || {};
  const guardarAjustes = (op, nuevo) => {
    onCambiarOpcion(op.id, { tarjeta_ajustes: nuevo });
    programar(`op:${op.id}`, { tarjeta_ajustes: nuevo }, (p) => actualizarOpcion(op.id, p));
  };
  const editarPaso = (op, clave, parche) => {
    const a = ajustesDe(op);
    const previo = (a[modo] || {})[clave] || {};
    guardarAjustes(op, { ...a, [modo]: { ...(a[modo] || {}), [clave]: { ...previo, ...parche } } });
  };
  const restablecerPaso = (op, clave) => {
    const a = ajustesDe(op);
    const { [clave]: _quitado, ...resto } = a[modo] || {};
    guardarAjustes(op, { ...a, [modo]: resto });
  };
  const editarEtiqueta = (op, texto) => guardarAjustes(op, { ...ajustesDe(op), etiqueta: texto });

  const exportar = async (como) => {
    if (!elegidas.length || !tarjetaRef.current) return;
    setExportando(como);
    try {
      await flush();
      await esperarFuentes();
      const dataUrl = await toPng(tarjetaRef.current.firstElementChild, { pixelRatio: 2, cacheBust: false });
      const blob = await (await fetch(dataUrl)).blob();
      const r = await entregarArchivo({ blob, nombre: nombreArchivo(cot.nombre, modo), mime: 'image/png', modo: como });
      if (r === 'cancelado') { setExportando(''); return; }
      if (como === 'compartir' && r === 'descargado') toast('Este dispositivo no permite compartir: se descargó el archivo.');
      toast('Imagen lista.');
    } catch { toast('No se pudo generar la imagen.', 'error'); }
    setExportando('');
  };

  return (
    <div>
      <style>{CSS_ARTE}</style>
      <style>{TARJETA_CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={async () => { await flush(); onBack(); }} style={btnChico}>← Volver a la cotización</button>
        <IndicadorGuardado estado={estado} />
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Crear orçamento</h3>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.45 }}>
        Marca hasta {MAX_OPCIONES} opciones y elige el modo. Nunca salen ganancia, costos, comisión, hostal ni de dónde sacaste el pasaje.
      </p>

      <div style={cardStyle}>
        <label style={labelStyle}>Opciones (de 1 a {MAX_OPCIONES})</label>
        <div style={{ display: 'grid', gap: 8 }}>
          {cot.opciones.map((op, i) => {
            const on = ids.includes(op.id);
            const bloqueada = !validas[i] || (!on && ids.length >= MAX_OPCIONES);
            const r = resultados[i];
            return (
              <button key={op.id} type="button" disabled={bloqueada && !on} onClick={() => setIds((l) => alternarOpcion(l, op.id))} style={{
                display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', minHeight: 56, padding: '10px 12px', borderRadius: 14,
                background: on ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)', border: `1.5px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`,
                color: 'var(--white)', opacity: bloqueada && !on ? 0.5 : 1, cursor: bloqueada && !on ? 'not-allowed' : 'pointer',
              }}>
                <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, background: on ? 'var(--orange)' : 'transparent', color: '#fff', fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on ? '✓' : ''}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
                    {fmtFecha(op.dia_vuelo)} <span style={{ fontSize: 11, color: 'var(--muted)' }}>{diaSemana(op.dia_vuelo)}</span>
                    {op.ciudad ? <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}> · {op.ciudad}</span> : null}
                  </span>
                  {resumenFila(op) && <span style={{ display: 'block', fontSize: 12, color: 'var(--off-white)' }}>{resumenFila(op)}</span>}
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                    {!validas[i] ? (op.hora_vuelo ? 'Ningún ómnibus llega a tiempo' : 'Falta la hora del vuelo')
                      : esRev ? `Precio R$ ${r.precioFinal === null ? '—' : r.precioFinal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Vuelo ' + (op.hora_vuelo || '—')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {ids.length >= MAX_OPCIONES && <p style={{ fontSize: 11, color: 'var(--gold)', margin: '8px 0 0' }}>Máximo {MAX_OPCIONES} opciones. Desmarca una para elegir otra.</p>}
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>Modo</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {[['basico', 'Básico'], ['detallado', 'Detallado']].map(([m, t]) => (
            <button key={m} type="button" className="ml-chip" aria-pressed={modo === m} onClick={() => elegirModo(m)} style={{ flex: 1, justifyContent: 'center' }}>{t}</button>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.45 }}>
          {modo === 'basico'
            ? 'Para mandar antes de que pague: solo el día de cada tramo, sin empresa, horas ni terminales de ómnibus, y sin número de vuelo.'
            : 'Para entregar cuando ya pagó: agrega empresa, horas y terminales, número de vuelo, aeropuertos y localizador.'}
        </p>
        <Interruptor activo={!!cot.pagado} onChange={marcarPagado}>Este cliente ya pagó</Interruptor>
        <Interruptor activo={mostrarPrecio} onChange={setMostrarPrecio}>Mostrar precio</Interruptor>
        {!esRev && mostrarPrecio && (
          <Interruptor activo={mostrarDolares} onChange={setMostrarDolares}>Mostrar dólares (a la tasa {String(cot.tasa).replace('.', ',')})</Interruptor>
        )}
        <Campo label={`Pasajeros (uno por línea${modo === 'detallado' ? '; el nombre solo sale en el Detallado' : ''})`} style={{ marginTop: 10 }}>
          <textarea value={pasajerosTxt} rows={2} onChange={(e) => editarPasajeros(e.target.value)} placeholder="Nombre completo" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 }} />
        </Campo>
        <Campo label="Nota final" style={{ marginTop: 10 }}>
          <textarea value={nota} rows={3} onChange={(e) => setNota(e.target.value)} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 }} />
        </Campo>
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>Esto lo ve el cliente: no pongas CPF ni datos de pago.</p>
      </div>

      {modelo.avisos.length > 0 && (
        <div style={{ ...cardStyle, borderColor: 'var(--error)' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--error)', marginBottom: 4 }}>⚠ Revisa antes de mandar:</p>
          {modelo.avisos.map((a) => <p key={a} style={{ fontSize: 12, color: 'var(--off-white)', lineHeight: 1.5, margin: '0 0 4px' }}>{a}</p>)}
        </div>
      )}

      {modelo.faltantes.length > 0 && (
        <div style={{ ...cardStyle, borderColor: 'var(--gold)' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)', marginBottom: 4 }}>Falta (esa línea no se imprime):</p>
          <p style={{ fontSize: 12, color: 'var(--off-white)', lineHeight: 1.5 }}>{modelo.faltantes.join(' · ')}</p>
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Vista previa</label>
          <button type="button" onClick={() => setEditar(!editar)} style={{ ...btnChico, padding: '6px 10px' }}>{editar ? 'Cerrar textos' : 'Editar textos'}</button>
        </div>

        {editar && elegidas.map((i) => {
          const op = cot.opciones[i];
          const t = modelo.opciones[elegidas.indexOf(i)];
          const a = (ajustesDe(op)[modo]) || {};
          return (
            <div key={op.id} style={{ border: '1px dashed var(--navy-border)', borderRadius: 12, padding: 10, marginBottom: 10 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', marginBottom: 6 }}>{fmtFecha(op.dia_vuelo)} · {op.ciudad || 'Opción'}</p>
              <Campo label="Etiqueta (vacío = automática)" style={{ marginBottom: 8 }}>
                <input value={ajustesDe(op).etiqueta || ''} onChange={(e) => editarEtiqueta(op, e.target.value)} placeholder={t?.tag?.texto || ''} style={inputStyle} />
              </Campo>
              {t?.pasos.map((p) => (
                <div key={p.clave} style={{ marginBottom: 8 }}>
                  <input value={a[p.clave]?.titulo ?? p.titulo} onChange={(e) => editarPaso(op, p.clave, { titulo: e.target.value, lineas: a[p.clave]?.lineas ?? p.lineas })} style={{ ...inputStyle, fontWeight: 700 }} />
                  {(p.lineas.length > 0 || a[p.clave]?.lineas) && (
                    <textarea
                      value={(a[p.clave]?.lineas ?? p.lineas).join('\n')} rows={Math.max(1, (a[p.clave]?.lineas ?? p.lineas).length)}
                      onChange={(e) => editarPaso(op, p.clave, { titulo: a[p.clave]?.titulo ?? p.titulo, lineas: e.target.value.split('\n') })}
                      style={{ ...inputStyle, marginTop: 4, fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }}
                    />
                  )}
                  {p.editado && <button type="button" onClick={() => restablecerPaso(op, p.clave)} style={{ ...btnChico, padding: '4px 8px', marginTop: 4 }}>Restablecer este tramo</button>}
                </div>
              ))}
            </div>
          );
        })}

        {elegidas.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '20px 0' }}>Marca al menos una opción para ver la tarjeta.</p>
        ) : (
          <div ref={contenedor} style={{ width: '100%', height: alto * escala, overflow: 'hidden', borderRadius: 12, border: '1px solid var(--navy-border)' }}>
            <div ref={tarjetaRef} style={{ width: 1080, transform: `scale(${escala})`, transformOrigin: 'top left' }} dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        )}
        {elegidas.length === 0 && <div ref={contenedor} style={{ height: 0 }}><div ref={tarjetaRef} /></div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button type="button" className="ml-btn btn-primary" disabled={!elegidas.length || !!exportando} onClick={() => exportar('bajar')} style={{ flex: 1 }}>
            {exportando === 'bajar' ? 'Generando…' : 'Descargar PNG'}
          </button>
          <button type="button" className="ml-btn" disabled={!elegidas.length || !!exportando} onClick={() => exportar('compartir')} style={{ flex: 1, background: 'transparent', border: '1.5px solid var(--orange)', color: 'var(--orange)' }}>
            {exportando === 'compartir' ? 'Generando…' : 'Compartir'}
          </button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '8px 0 0' }}>
          La imagen se arma en tu teléfono y no se guarda en ningún lugar público. Ancho 1080 px, nítida a 2x.
        </p>
      </div>
    </div>
  );
}
